import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ClientProvider } from './context/ClientContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Navigation } from './components/Navigation';
import { AIAssistant } from './components/AIAssistant';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { AuthCallback } from './pages/AuthCallback';
import { Landing } from './pages/Landing';
import { IpadPreview } from './pages/IpadPreview';
import CashClosePublic from './pages/CashClosePublic';
import R3FProbe from './pages/R3FProbe';
import { Dashboard } from './pages/Dashboard';
import { ClientManagement } from './pages/ClientManagement';
import { MenuEngineering } from './pages/MenuEngineering';
import { Suppliers } from './pages/Suppliers';
import Reconciliation from './pages/Reconciliation';
import { Files } from './pages/Files';
import { Recipes } from './pages/Recipes';
import { MandiPrices } from './pages/MandiPrices';
import { MandiMapping } from './pages/MandiMapping';
import { Expenses } from './pages/Expenses';
import { Reviews } from './pages/Reviews';
import { FinancialAnalysis } from './pages/FinancialAnalysis';
import { Reports } from './pages/Reports';
import { Movements } from './pages/Movements';
import { Benchmarks } from './pages/Benchmarks';
import { Tally } from './pages/Tally';
import { POSIntegrations } from './pages/POSIntegrations';
import { AccountingIntegrations } from './pages/AccountingIntegrations';
import { ForgotPassword } from './pages/ForgotPassword';
import { AuditLog } from './pages/AuditLog';
import { Security } from './pages/Security';
import { AdminFiles } from './pages/AdminFiles';
import { AdminAllowlist } from './pages/AdminAllowlist';
import { AdminCosts } from './pages/AdminCosts';
import { Exceptions } from './pages/Exceptions';
import { DailyRecon } from './pages/DailyRecon';
import { LinksAdmin } from './pages/LinksAdmin';
import { ScanPublic } from './pages/ScanPublic';
import { Toaster } from './components/ui/sonner';
import './App.css';

// Paths a `role=client` user is allowed to visit. Everything else silently
// redirects back to /dashboard so operator-only surfaces stay invisible to
// clients — no toast, no 403 screen — even when a URL is typed or bookmarked.
const CLIENT_ALLOWED_ROUTES = new Set([
  '/dashboard',
  '/financial-analysis',
  '/reports',
  '/menu',
  '/movements',
  '/recipes',
  '/suppliers',
  '/expenses',
  '/reconciliation',
  '/daily-recon',
  '/reviews',
]);

const RoleGate = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const role = user?.role;
  if (role === 'client' && !CLIENT_ALLOWED_ROUTES.has(location.pathname)) {
    // Silent redirect — no toast, no error screen. Operator-only surfaces
    // must be invisible to clients even when a URL is typed or bookmarked.
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// Watches the sidebar collapse state stored in localStorage to set main-content left padding.
const useSidebarMode = () => {
  const [mode, setMode] = React.useState(() => {
    try { return localStorage.getItem('sidebarMode') || 'expanded'; }
    catch (_) { return 'expanded'; }
  });
  React.useEffect(() => {
    const handler = () => {
      try { setMode(localStorage.getItem('sidebarMode') || 'expanded'); }
      catch (_) {}
    };
    window.addEventListener('storage', handler);
    // Also poll briefly because same-tab localStorage changes don't fire 'storage'
    const id = setInterval(handler, 400);
    return () => { window.removeEventListener('storage', handler); clearInterval(id); };
  }, []);
  return mode;
};

const AppLayout = () => {
  const sidebarMode = useSidebarMode();
  const sidebarPad = sidebarMode === 'collapsed' ? 'lg:pl-16' : 'lg:pl-60';
  return (
    <ClientProvider>
      <Navigation />
      <main
        className={`${sidebarPad} transition-[padding] duration-200 ease-in-out`}
        data-testid="main-content"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          <RoleGate>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/menu" element={<MenuEngineering />} />
            <Route path="/menu-engineering" element={<MenuEngineering />} />
            <Route path="/recipes" element={<Recipes />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/mandi-prices" element={<MandiPrices />} />
            <Route path="/mandi-mapping" element={<MandiMapping />} />
            <Route path="/reconciliation" element={<Reconciliation />} />
            <Route path="/files" element={<Files />} />
            <Route path="/expenses" element={<Expenses />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/financial-analysis" element={<FinancialAnalysis />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/movements" element={<Movements />} />
            <Route path="/benchmarks" element={<Benchmarks />} />
            <Route path="/tally" element={<Tally />} />
            <Route path="/pos-integrations" element={<POSIntegrations />} />
            <Route path="/accounting-integrations" element={<AccountingIntegrations />} />
            <Route path="/security" element={<Security />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/admin/files" element={<AdminFiles />} />
            <Route path="/admin/allowlist" element={<AdminAllowlist />} />
            <Route path="/admin/costs" element={<AdminCosts />} />
            <Route path="/exceptions" element={<Exceptions />} />
            <Route path="/daily-recon" element={<DailyRecon />} />
            <Route path="/links" element={<LinksAdmin />} />
            <Route path="/clients" element={<ClientManagement />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </RoleGate>
        </div>
      </main>
      <AIAssistant />
    </ClientProvider>
  );
};

function App() {
  return (
    <div className="App min-h-screen bg-background">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/_preview/ipad" element={<IpadPreview />} />
            {/* Daily Recon M2 · Public cash-close (no login). */}
            <Route path="/cash-close/:token" element={<CashClosePublic />} />
            <Route path="/scan/:token" element={<ScanPublic />} />
            <Route path="/_preview/r3f" element={<R3FProbe />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </div>
  );
}

export default App;
